import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {useLocalStorage, useToggle} from "../hooks";
import {backend} from "../_services";
import {scrollToTop} from "../utils2";

const AuthContext = createContext(null);

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({children}) {

    const refresh_token_id = useRef([])
    const [loading, setLoading] = useToggle(false);

    const [authToken, setAuthToken] = useLocalStorage("auth_token", {jwt: null, tmo: null});
    const [bskToken, setBskToken] = useLocalStorage("bsk_token", null);
    const [user, setUser] = useLocalStorage("user", null);

    // EditUser's properties
    const loggedIn = !!(authToken?.jwt && authToken?.tmo && user);
    const userId = loggedIn ? user.id : null;
    const admin = loggedIn && user.role === "admin";

    function getTimeout(tmo) {
        return new Date(new Date().getTime() + tmo * 1000).toLocaleString("en-US", {timeZoneName: "short"})
    }


    function setRefresh(token) {
        backend.refreshToken(token.jwt)
            .then(resp => {
                if (resp.success) {
                    setAuthToken(j => (resp.success && j) ? resp.data : null)

                    // Refresh timeout 10% before timeout
                    const authToken = resp.data
                    const timeout = authToken?.tmo ? authToken.tmo - (authToken.tmo * 0.1) : 60
                    const timeoutId = setTimeout(() => setRefresh(authToken), timeout * 1000) // Milliseconds
                    console.debug(`Next refresh-token scheduled in ${timeout} sec at ${getTimeout(timeout)}`)

                    // Make sure to remove all previous scheduled refresh and then add it the list
                    clearRefresh()
                    refresh_token_id.current.push(timeoutId)
                    //console.debug(`Schedule list length ${refresh_token_id.current.length}`)
                } else {
                    return Promise.reject(resp)
                }
            })
            .catch(err => {
                console.warn("Refresh-token exception: ", err)
                setAuthToken(null)
                clearRefresh()
            })
    }

    function clearRefresh() {
        //console.debug(`Remove ${refresh_token_id.current.length} scheduled refresh-token callback.`)
        refresh_token_id.current.forEach(clearTimeout);
        refresh_token_id.current = []
    }

    /**
     * At app launch, if a token is in the Browser local storage, automatically starts the token refresh.
     * Alternatively, continuously refresh the token at login time.
     */
    useEffect(() => {
        if (authToken) {
            setRefresh(authToken)
        } else {
            setUser(null)
        }

        return clearRefresh
    }, []);


    /*
     * Lambda short functions
     */
    const handleSuccess = resp => {
        if (resp.success) {
            return Promise.resolve(resp?.data);
        } else {
            if ("message" in resp) {
                return Promise.reject({message: resp.message});
            } else {
                return Promise.reject({message: "Failed with no error messages from the server."});
            }
        }
    }

    const printErrors = (err) => {
        console.error(err);
        return err
        // error(<p>Error {err.error}<br/>{err.message}</p>, "Internal server error");
    }

    const _finally = () => {
        setLoading(false);
        scrollToTop();
    }


    /*
     * Exposed functions
     */
    function signup(formValues) {
        setLoading(true);
        return backend.signup(formValues).catch(printErrors).finally(_finally);
    }

    function login(formValues) {
        setLoading(true);
        const payload = {...formValues, bsk_token: bskToken}
        return backend.login(payload)
            .then(resp => {
                if (resp.success) {
                    setAuthToken(resp.data.auth_token)
                    setRefresh(resp.data.auth_token)
                    setBskToken(resp.data.bsk_token)
                    setUser({...resp.data.user, password: ""})
                    console.log(`User ${resp.data.user.username} logged in.`)
                    // return Promise.resolve(resp.data.user);
                    return resp.data.user
                }
                return Promise.reject({message: resp.message})
            })
            .catch(err => {
                setAuthToken(null)
                clearRefresh()
                setBskToken(null)
                return Promise.reject(err)
            })
            .finally(_finally);
    }

    function logout() {
        setLoading(true)
        return backend.logout()
            .finally(() => {
                setAuthToken(null)
                clearRefresh()
                setBskToken(null)
                setLoading(false)
                setUser(null)
                scrollToTop()
            });
    }

    function forgotPassword(formValues) {
        setLoading(true);
        // Propagate .then and .catch to the caller
        return backend.forgotPassword(formValues).catch(printErrors).finally(_finally);
    }

    function resetPassword(formValues) {
        setLoading(true);
        return backend.resetPassword(formValues).catch(printErrors).finally(_finally);
    }


    function verifyEmail(formValues) {
        setLoading(true);
        // Propagate .then and .catch to the caller
        const payload = {jwt: formValues.token}
        return backend.verifyEmail(payload).catch(printErrors).finally(_finally);
    }

    function resendVerificationEmail(formValues) {
        setLoading(true);
        // Propagate .then and .catch to the caller
        return backend.resendEmail(formValues).catch(printErrors).finally(_finally);
    }

    function updateUserSelf(first_name, last_name, location, username, password, role = null, verified = null, blocked = null) {
        setLoading(true);
        const payload = {first_name, last_name, location, username, password, role, verified, blocked}
        return backend.editUser(authToken?.jwt, userId, userId, payload)
            .then(handleSuccess)
            .then(data => {
                setUser(user => String(data.id) === userId ? {...user, ...data} : user)
                return data
            })
            .finally(_finally);
    }

    function deleteUserSelf() {
        setLoading(true);
        // userId and targetUserId are the same for self delete
        return backend.deleteUser(authToken?.jwt, userId, userId).finally(_finally);
    }

    function setVatPercentage(vat_percentage, vat_defined) {
        setUser(prev => ({...prev, vat_percentage: vat_percentage, vat_defined: vat_defined}))
    }

    const value = {
        // Login
        login,
        logout,

        // EditUser signup
        signup,
        forgotPassword,
        resetPassword,
        verifyEmail,
        resendVerificationEmail,

        // EditUser manipulation
        updateUserSelf,
        deleteUserSelf,

        // Shop related
        setVatPercentage,

        // Variables
        user,
        authToken,
        token: authToken,
        bskToken,
        setBskToken,
        loading,
        loggedIn,
        userId,
        admin
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

